import { useState, useEffect, useRef } from 'react';
import { Phone, Copy, Check, Save, Pencil, X, Loader as Loader2, Maximize2 } from 'lucide-react';
import type { CallBattlecard } from '../../../types';

interface Props {
  battlecard: CallBattlecard;
  onSave: (updated: CallBattlecard) => Promise<void>;
  hasUnsavedChanges: (fieldKey: string, dirty: boolean) => void;
}

const SECTION_LABELS: { key: keyof CallBattlecard; label: string; color: string }[] = [
  { key: 'social_proof', label: 'Social Proof', color: 'text-app-positive' },
  { key: 'situation', label: 'Situation', color: 'text-app-focus' },
  { key: 'pain', label: 'Pain', color: 'text-app-critical' },
  { key: 'impact', label: 'Impact', color: 'text-app-warning' },
  { key: 'how_we_help', label: 'How We Help', color: 'text-forest-600' },
];

function formatBattlecardText(bc: CallBattlecard): string {
  const lines: string[] = [];
  lines.push('SOCIAL PROOF');
  lines.push(bc.social_proof || '(none)');
  lines.push('');
  lines.push('SITUATION');
  lines.push(bc.situation);
  lines.push('');
  lines.push('PAIN');
  bc.pain.forEach(p => lines.push(`  - ${p}`));
  lines.push('');
  lines.push('IMPACT');
  bc.impact.forEach(i => lines.push(`  - ${i}`));
  lines.push('');
  lines.push('HOW WE HELP');
  bc.how_we_help.forEach(h => lines.push(`  - ${h}`));
  return lines.join('\n');
}

function BulletList({ items, editing, onChange, large }: {
  items: string[];
  editing: boolean;
  onChange: (items: string[]) => void;
  large?: boolean;
}) {
  if (!editing) {
    const textSize = large ? 'text-base' : 'text-sm';
    const spacing = large ? 'space-y-3' : 'space-y-2.5';
    const dotSize = large ? 'mt-2.5 w-2 h-2' : 'mt-2 w-1.5 h-1.5';
    return (
      <ul className={spacing}>
        {items.map((item, i) => (
          <li key={i} className={`flex items-start gap-2.5 ${textSize} text-app-ink leading-relaxed`}>
            <span className={`${dotSize} rounded-full bg-app-positive flex-shrink-0`} />
            {item}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-app-muted flex-shrink-0" />
          <textarea
            value={item}
            onChange={e => {
              const updated = [...items];
              updated[i] = e.target.value;
              onChange(updated);
            }}
            className="w-full text-sm text-app-ink leading-relaxed bg-app-surface border border-app-line-strong rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-forest-300 focus:border-forest-400 resize-none transition-colors"
            rows={2}
          />
        </div>
      ))}
    </div>
  );
}

export default function CallBattlecardCard({ battlecard, onSave, hasUnsavedChanges }: Props) {
  const [draft, setDraft] = useState<CallBattlecard>(battlecard);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEditing, setModalEditing] = useState(false);
  const [modalDraft, setModalDraft] = useState<CallBattlecard>(battlecard);
  const [modalSaving, setModalSaving] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(battlecard);
    setEditing(false);
  }, [battlecard]);

  useEffect(() => {
    setModalDraft(battlecard);
    setModalEditing(false);
  }, [battlecard]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(battlecard);
  const isModalDirty = JSON.stringify(modalDraft) !== JSON.stringify(battlecard);

  useEffect(() => {
    hasUnsavedChanges('call_battlecard', (editing && isDirty) || (modalOpen && modalEditing && isModalDirty));
  }, [editing, isDirty, modalOpen, modalEditing, isModalDirty, hasUnsavedChanges]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(formatBattlecardText(battlecard));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(battlecard);
    setEditing(false);
  };

  const handleModalSave = async () => {
    setModalSaving(true);
    await onSave(modalDraft);
    setModalSaving(false);
    setModalEditing(false);
  };

  const handleModalCancel = () => {
    setModalDraft(battlecard);
    setModalEditing(false);
  };

  const openModal = () => {
    setModalDraft(battlecard);
    setModalEditing(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (modalEditing && isModalDirty) {
      const confirmed = window.confirm('You have unsaved changes. Discard?');
      if (!confirmed) return;
    }
    setModalOpen(false);
    setModalEditing(false);
  };

  useEffect(() => {
    if (!modalOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [modalOpen, modalEditing, isModalDirty]);

  const updateDraftField = (key: keyof CallBattlecard, value: string | string[]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const updateModalDraftField = (key: keyof CallBattlecard, value: string | string[]) => {
    setModalDraft(prev => ({ ...prev, [key]: value }));
  };

  const renderSection = (
    key: keyof CallBattlecard,
    label: string,
    color: string,
    bc: CallBattlecard,
    isEditing: boolean,
    onUpdate: (key: keyof CallBattlecard, value: string | string[]) => void,
    large?: boolean,
  ) => {
    const value = bc[key];
    const isSocialProof = key === 'social_proof';
    if (!isEditing && !isSocialProof) {
      if (!value) return null;
      if (Array.isArray(value) && value.length === 0) return null;
      if (typeof value === 'string' && !value.trim()) return null;
    }

    const textSize = large ? 'text-base' : 'text-sm';
    const labelSize = large ? 'text-sm' : 'text-xs';
    const isEmpty = !value || (typeof value === 'string' && !value.trim());

    return (
      <div key={key} className="pt-1">
        <p className={`${labelSize} font-bold ${color} mb-2`}>{label}</p>
        {Array.isArray(value) ? (
          <BulletList
            items={value}
            editing={isEditing}
            onChange={items => onUpdate(key, items)}
            large={large}
          />
        ) : isEditing ? (
          <textarea
            value={value}
            onChange={e => onUpdate(key, e.target.value)}
            placeholder={isSocialProof ? 'Enter names of people you have spoken to at similar organisations...' : ''}
            className={`w-full ${textSize} text-app-ink leading-relaxed bg-app-surface border border-app-line-strong rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-forest-300 focus:border-forest-400 resize-none transition-colors`}
            rows={isSocialProof ? 2 : 3}
          />
        ) : isEmpty && isSocialProof ? (
          <p className={`${textSize} text-app-muted italic leading-relaxed`}>No social proof available. Click Edit to add your own.</p>
        ) : (
          <p className={`${textSize} text-app-ink leading-relaxed`}>{value}</p>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="rounded-lg border shadow-none overflow-hidden bg-app-positive/10 border-app-positive">
        <div className="bg-app-positive px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-white" />
            <h4 className="text-sm font-semibold text-white">Call Battlecard</h4>
          </div>
          <div className="flex items-center gap-1">
            {!editing ? (
              <>
                <button
                  onClick={openModal}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
                >
                  <Maximize2 className="w-3 h-3" />
                  Expand
                </button>
                <button
                  onClick={() => { setDraft(battlecard); setEditing(true); }}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
                >
                  {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-app-surface/20 hover:bg-app-surface/30 rounded transition-colors disabled:opacity-40"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save
                </button>
              </>
            )}
          </div>
        </div>
        <div className="p-5 space-y-5 divide-y divide-emerald-100">
          {SECTION_LABELS.map(({ key, label, color }) =>
            renderSection(key, label, color, draft, editing, updateDraftField)
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
          <div
            ref={modalRef}
            className="relative bg-app-surface rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4"
          >
            <div className="sticky top-0 bg-app-positive px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-white" />
                <h3 className="text-base font-semibold text-white">Call Battlecard</h3>
              </div>
              <div className="flex items-center gap-2">
                {!modalEditing ? (
                  <>
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
                    >
                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? 'Copied' : 'Copy All'}
                    </button>
                    <button
                      onClick={() => { setModalDraft(battlecard); setModalEditing(true); }}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-app-surface/20 hover:bg-app-surface/30 rounded transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleModalCancel}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                    <button
                      onClick={handleModalSave}
                      disabled={modalSaving || !isModalDirty}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-app-surface/20 hover:bg-app-surface/30 rounded transition-colors disabled:opacity-40"
                    >
                      {modalSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Save
                    </button>
                  </>
                )}
                <button
                  onClick={closeModal}
                  className="p-1 text-white/70 hover:text-white hover:bg-app-surface/10 rounded transition-colors ml-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-8 space-y-7 divide-y divide-app-line">
              {SECTION_LABELS.map(({ key, label, color }) =>
                renderSection(key, label, color, modalDraft, modalEditing, updateModalDraftField, true)
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
